import { AlertVariantType } from '../common/components/Alert';
import FormModel from '../../utils/FormModel';
import { IStateForm, AbstractFormState, AbstractForm } from '../common/AbstractForm';
import PortOpeningRequestOnPrem from '../../models/entities/PortOpeningRequestOnPrem';
import UserLookupObject from '../../models/UserLookupObject';
import { LinkTreeItem } from '../common/LinkTree';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import { Params, Location } from 'react-router-dom';
import OnPremPortOpeningRequestService from '../../services/OnPremPortOpeningRequestService';
import PortOpeningRuleOnPrem from '../../models/entities/PortOpeningRuleOnPrem';
import CartaId from '../../models/entities/CartaId';
import { OnPremPortOpeningRequestsTypes } from '../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import CartaService from '../../services/CartaService';
import CartaIdComponent from '../../models/viewModels/CartaId/CartaIdComponent';
import { authenticatedUser } from '../../authentication/authModule';
import { Address4 } from 'ip-address';
import { getCartaIdAppName } from '../common/CartaIdAppStorage';
import EnvironmentConfiguration from '../../utils/EnvironmentConfiguration';
import OnPremRiskPorts from '../../models/entities/OnPremRiskPorts';

// Caches the risk ports lookup across mounts/instances
const risksPortsCache: { value?: OnPremRiskPorts } = { value: undefined };

interface EndModal extends AlertBanner {
	redirectionLink?: string;
}

export interface AbstractPORFormProps {
	params?: Params<string>;
	navigate?: any;
	location?: Location;
}

export interface IPORStateForm extends IStateForm {
	selectedType?: string;
	direction?: string;
	contact?: UserLookupObject | null;
	approverDOAG?: UserLookupObject | null;
	trackerID?: number;
	cartaId: CartaId[] | null;
	appEndorser?: CartaIdComponent | null;
	firewall: string; //Firewall[] || null;
	firewallEnvironment?: string;
	ownerDelegate?: string;
	siteCoordination?: boolean;
	businessJustification: string;
	comments?: string;
	ruleList: PortOpeningRuleOnPrem[];
	changeTicketId?: string;
	hasRiskPorts?: boolean;
}

export interface AbstractPORFormState<T> extends AbstractFormState<T> {
	operationInProgress: boolean;
	endModal: EndModal;
	submissionWarned: boolean;
	submissionAlert: AlertBanner;
	appEndorserOptions?: any;
	approverDelegateOptions?: any;
	firewallOptions?: any;
	loadingTracker?: boolean;
	failedTracker?: boolean,
	errorTracker?: string;
	implementedRules: PortOpeningRuleOnPrem[];
	loadingImplementedRules: boolean;
	failedImplementedRules: boolean;
}

export abstract class OnPremAbstractPORForm<
	Props extends AbstractPORFormProps,
	State extends AbstractPORFormState<IPORStateForm>
	> extends AbstractForm<Props, State> {

	portOpeningRequestServiceOnPrem: OnPremPortOpeningRequestService;
	cartaService: CartaService;
	lastTrackerId?: number;

	constructor(props) {
		super(props);
		this.portOpeningRequestServiceOnPrem = new OnPremPortOpeningRequestService();
		this.cartaService = new CartaService();
	}

	getLinkTree = (): LinkTreeItem[] => {
		return [
			{ text: 'Port Opening Requests', to: '/onPremPortOpeningRequests' },
		];
	};

	initFormModel() {
		this.formModel = new FormModel({
			direction: {
				getValue: () => this.state.stateForm.direction,
				validation: {
					required: true
				}
			},
			contactId: {
				getValue: () => this.state.stateForm.contact?.id,
			},
			approverId: {
				getValue: () =>
					this.state.stateForm?.selectedType=== OnPremPortOpeningRequestsTypes.InternetDMZ.name ? this.state.stateForm.approverDOAG?.id :
				this.state.stateForm.ownerDelegate,
				validation: {
					required: false
				}
			},
			trackerID: {
				getValue: () => this.state.stateForm.trackerID,
			},
			cartaId: {
				getValue: () => this.state.stateForm.cartaId,
			},
			appEndorserId: {
				getValue: () => this.state.stateForm.appEndorser?.customFields?.contact_email,
			},
			firewall: {
				getValue: () => this.state.stateForm.firewall,
			},
			firewallEnvironment: {
				getValue: () => this.state.stateForm.firewallEnvironment,
			},
			ownerDelegate: {
				getValue: () => this.state.stateForm.ownerDelegate,
			},
			siteCoordination: {
				getValue: () => this.state.stateForm.siteCoordination,
			},
			changeTicketId: {
				getValue: () => this.state.stateForm.changeTicketId,				
			},
			hasRiskPorts: {
				getValue: () => this.state.stateForm.hasRiskPorts,				
			},
			businessJustification: {
				getValue: () => this.state.stateForm.businessJustification,
				validation: {
					required: true,
					rules: [
						{
							assert: () =>
								this.state.stateForm.businessJustification?.trim().length > 4,
							message: 'Justification must contain at least 5 characters.',
						},
					],
				},
			},			
			portOpeningRules: {
				getValue: () => this.state.stateForm.ruleList,
				validation: {
					required: true
				}
			},
			comments: {
				getValue: () => this.state.stateForm.comments,
			},
		});
	}

	stateFormHandler = () => this.getStateFormHandlers();

	shouldSetImplementedRules() {
		if (this.state.stateForm.selectedType == 'InternetDMZ') {
			return this.state.stateForm.cartaId.length != 0 && this.state.stateForm.direction != null;
		} else if (this.state.stateForm.selectedType == 'InternalCertConnection') {
			return (this.state.stateForm.trackerID != null && this.state.stateForm.trackerID > 10000) && this.state.stateForm.direction != null;
		}
		return false;
	}
	
	async emptyImplementedRules() {
		this.setState({
			implementedRules: [],
			loadingImplementedRules: false,
			failedImplementedRules: false
		});
	}

	async getRisksPorts() {
		if (typeof risksPortsCache.value !== 'undefined') return risksPortsCache.value;
		const risksPorts = await this.portOpeningRequestServiceOnPrem.getRisksPorts();
		risksPortsCache.value = risksPorts;
		return risksPorts;
	}
	
	async setImplementedRules() {
		if (this.shouldSetImplementedRules()) {
			try {
				if (this.state.stateForm.selectedType == 'InternetDMZ')		
					var appId = this.state.stateForm.cartaId.length > 0 ? Number(this.state.stateForm.cartaId[0].cartaAppId.split("-")[1]) : 0;
				const implementedRules = await this.portOpeningRequestServiceOnPrem.getImplementedRulesByTrackerIdAndDBMId(this.state.stateForm.selectedType,
					this.state.stateForm.direction,
					this.state.stateForm.trackerID == null ? 0 : this.state.stateForm.trackerID,
					appId);

				this.setState({
					implementedRules,
					loadingImplementedRules: false,
					failedImplementedRules: false
				});
			} catch (error) {
				this.setState({
					failedImplementedRules: true
				})
				this.handleEndModal('Error while retrieving info, please try again', 'error', 5000, `/onPremPortOpeningRequests`);
			}
		}
	}

	getStateFormHandlers() {
		return {
			direction: {
				value: this.state.stateForm.direction,
				validation: this.formModel.fields.direction.validation,
				onChange: (event) => {
					this.handleStateFormChange('direction', event.target.value);
				}
			},
			contact: {
				value: this.state.stateForm.contact?.userPrincipalName,
				validation: this.formModel.fields.contactId.validation,
				onChange: (value) => {
					this.handleStateFormChange('contact', value);
				}
			},
			approverDOAG: {
				value: this.state.stateForm.approverDOAG?.userPrincipalName,
				validation: this.formModel.fields.approverId.validation,
				onChange: (value) => {
					this.handleStateFormChange('approverDOAG', value);
				},
				higherApproval: false
			},
			trackerID: {
				value: this.state.stateForm.trackerID,
				validation: this.formModel.fields.trackerID.validation,
				onChange: async (event) => {
					if (event.target.value.length <= 5) {
						this.handleStateFormChange('trackerID', event.target.value);
						if (!(this.formModel.fields.trackerID.validation.rules.filter(x=>x.assert()===false).length>0) && !this.state.loadingTracker)
							this.handleApproverDelegateOptions(this.state.stateForm.trackerID)
					}
					this.setState({ failedTracker: false });
				},
			},
			cartaId: {
				value: this.state.stateForm.cartaId,
				validation: this.formModel.fields.cartaId.validation,
				onChange: (cartaIds) => {
					if (cartaIds.length === 1 && cartaIds[0]?.cartaAppId === this.state.stateForm.cartaId[0]?.cartaAppId) {
						const newComment = cartaIds[0].comment;
						cartaIds[0] = this.state.stateForm.cartaId[0];
						cartaIds[0].comment = newComment;
					}
					this.handleStateFormChange('cartaId', cartaIds);
					cartaIds.length === 1 && this.handleAppEndorserOptions(cartaIds[0]?.cartaAppId);
					cartaIds.length === 0 && this.handleStateFormChange('appEndorser', '');
				},
			},
			appEndorser:{
				value: this.state.stateForm.appEndorser?.customFields?.contact_email,
				validation: this.formModel.fields.appEndorserId.validation,
				options: this.state.appEndorserOptions ?? [],
				onChange: (event) => {
					this.handleStateFormChange('appEndorser', this.state.appEndorserOptions[event.target.selectedIndex-1].component);
				},
			},
			firewall: {
				value: this.state.stateForm.firewall,
				validation: this.formModel.fields.firewall.validation,
				options: this.state.firewallOptions ?? [],
				onChange: (event) => {
					this.handleStateFormChange('firewall', event.target.value);
				},
			},
			firewallEnvironment: {
				value: this.state.stateForm.firewallEnvironment,
				validation: this.formModel.fields.firewallEnvironment.validation,
				options: [
					{ value: 'ICCP/IIT Internal DMZ', text: 'ICCP/IIT Internal DMZ'},
					{ value: 'XTO', text: 'XTO'},
					{ value: 'S2M Lab', text: 'S2M Lab'},
					{ value: 'MITAS', text: 'MITAS'},
					{ value: 'Other - INC, ENC LAbLan, Genetec PCS', text: 'Other - INC, ENC LAbLan, Genetec PCS'}
				],
				onChange: (event) => {
					this.handleStateFormChange('firewallEnvironment', event.target.value);
				},
			},
			ownerDelegate: {
				value: this.state.stateForm.ownerDelegate,
				validation: this.formModel.fields.approverDelegate?.validation,
				options: this.state.approverDelegateOptions ?? [],
				onChange: (event) => {
					this.handleStateFormChange('ownerDelegate', event.target.value);
				},
			},
			siteCoordination: {
				value: this.state.stateForm.siteCoordination?.toString(),
				validation: this.formModel.fields.siteCoordination.validation,
				onChange: (event) => {
					this.handleStateFormChange('siteCoordination', event.target.value === 'true');
				},
			},
			businessJustification: {
				value: this.state.stateForm.businessJustification,
				validation: this.formModel.fields.businessJustification.validation,
				onChange: (event) => {
					this.handleStateFormChange('businessJustification', event.target.value);
				}
			},
			ruleset: {
				rules: this.state.stateForm.ruleList,
				onChange: (rules: PortOpeningRuleOnPrem[]) => {
					this.handleStateFormChange('ruleList', rules);
				}
			},
			comments: {
				value: this.state.stateForm.comments || '',
				validation: this.formModel.fields.comments.validation,
				onChange: (event) => {
					this.handleStateFormChange('comments', event.target.value);
				}
			},
			changeTicketId: {
				value: this.state.stateForm.changeTicketId,
				validation: this.formModel.fields.changeTicketId.validation,
				onChange: (event) => {
					this.handleStateFormChange('changeTicketId', event.target.value);
				}
			},
			hasRiskPorts: {
				value: this.state.stateForm.hasRiskPorts,
				onChange: (value) => {
					this.handleStateFormChange('hasRiskPorts', value);
				}
			},
		};
	}

	handleAppEndorserOptions = async (cartaAppId: string) => {
		const cartaIdComponent = await this.cartaService.getAppEndorsers(cartaAppId);
		const options = [];
		cartaIdComponent.map(component => {
			options.push({ value: component.customFields.contact_email, text: component.customFields.first_name + ' ' + component.customFields.last_name, component: component })
		});
		this.setState({ appEndorserOptions: options });
		return options;
	}

	handleApproverDelegateOptions = async (trackerId: number, fromReview: boolean = false) => {
		if (trackerId === this.lastTrackerId)
			return;
		try
		{
			this.setState({ loadingTracker: true });

			const networkConnection = await this.portOpeningRequestServiceOnPrem.getNCDBbyTrackerId(trackerId);
			const firewallOptions= [];
			if (networkConnection.childName != null) {
				networkConnection.childName.forEach(child => {
					firewallOptions.push({value: child, text: child})
				})
			}

			if (fromReview) firewallOptions.push({value: networkConnection.ncp, text: networkConnection.ncp})

			this.lastTrackerId = trackerId;
			const options = [];
			
			if (EnvironmentConfiguration.isDev())
			{
				options.push({ value: 'cb6cf014-cb6f-4eac-95eb-4824174c972d', text: 'Orellana, Martin'})
				options.push({ value: 'e9eb8912-7c8f-4765-ac9d-83cbc3b3e2f4', text: 'Gemelli De Sousa, Eduardo Jose'})
				options.push({ value: '5f35b0ef-c43a-4dd4-b39a-37caf241dbdf', text: 'Walter, Facundo'})
				options.push({ value: '7a8d5c85-4bf3-468d-9bb6-8ff6bb51dc8a', text: 'Sukhaidi, Izzah'})
				options.push({ value: 'd394914c-79cb-4cde-840c-b7bd3f01db53', text: 'Kamil, Hasrul Akmal'})
				options.push({ value: '51d1e816-746f-45be-8a07-cf1d00508b30', text: 'Hussain, Nazri'})
				options.push({ value: 'd8d9bf25-c8ce-4527-b93f-31f7ed1f32ee', text: 'Montiel Fernandez, Ricardo Jose'})
				options.push({ value: '2a4a3ef2-099b-4b79-87ec-e6f2c18e9e59', text: 'Ramon, Emilio Hugo'})
				options.push({ value: 'abb6de11-8046-4593-89d1-5edb27fe4340', text: 'Alegre Saban, Leandro Nicolas'})
				options.push({ value: '4d4c12fe-8cd5-4fce-8d2e-9df3c28a9436', text: 'Post, Jeff'})
				options.push({ value: 'b8032420-1914-4d03-8e27-fa61df630d63', text: 'Longin, Fernando' })
			}
			else 
			{
				if (networkConnection.owner?.id != null && networkConnection.owner?.id != authenticatedUser.userId)
				options.push({ value: networkConnection.owner.id, text: networkConnection.owner.displayName})

				// check if this is necessary or not
				networkConnection.delegate.filter(x=>x.id != authenticatedUser.userId).forEach(element => {
					options.push({value: element.id, text: element.displayName})
				});
			}
			this.setState({ approverDelegateOptions: options});
			this.setState({ firewallOptions: firewallOptions})
			//this.handleStateFormChange('firewall', networkConnection.ncp);

			this.setImplementedRules();

			return options;
		}
		catch(err)
		{
			const HTTP_NOT_FOUND_ERROR_CODE = 404;
			if (err.status == HTTP_NOT_FOUND_ERROR_CODE)
				this.lastTrackerId = trackerId;
			this.handleStateFormChange('firewall', null);

			this.setState({ failedTracker: true });
			this.setState({ errorTracker: err?.response?.data?.message ?? 'There was an error while retrieving Tracker Id' });

			this.setState({ firewallOptions: []})

			this.emptyImplementedRules()
		}
		finally{
			this.setState({ loadingTracker: false });
		}
	}

	async getCartaIdName (cartaId:string) {
		const setCartaAppName = (name: string) => {
			this.setState((prevState) => {
				const existing = prevState.stateForm.cartaId?.[0];
				if (!existing) return null;
				return {
					stateForm: {
						...prevState.stateForm,
						cartaId: [{ ...existing, cartaAppName: name }, ...prevState.stateForm.cartaId.slice(1)],
					},
				};
			});
		};

		setCartaAppName('Loading...');
		try {
			const cartaAppName = await getCartaIdAppName(cartaId, this.cartaService);
			setCartaAppName(cartaAppName);
		}
		catch (error){
			setCartaAppName('');
		}
	}

	handleEndModal = (text: string, variant: AlertVariantType, timeout: number, link: string) => {
		this.setState({
			endModal: {
				visible: true,
				text,
				variant,
				redirectionLink: link
			}
		},
		() => {
			setTimeout(() => {
				this.endAndRedirect();
			}, timeout);
		});
	};

	endAndRedirect = () => {
		const link = this.state.endModal.redirectionLink;
		this.setState({
			endModal: { visible: false }
		},
		() => {
			this.props.navigate(link);
		});
	};

	setSubmissionAlertInProgress = (message) => {
		window.scroll(0, 0);
		this.setState({
			operationInProgress: true,
			submissionAlert: {
				visible: true,
				text: message
			}
		});
	};

	setSubmissionAlertError = (message) => {
		this.setState({
			submissionAlert: {
				visible: true,
				text: message,
				variant: 'error'
			}
		});
	};

	handleSubmissionAlertClose = () => {
		this.setState({
			submissionAlert: {
				visible: false
			}
		});
	};

	requiresApproverVerification = (selectedType: string) => {
		return selectedType === OnPremPortOpeningRequestsTypes.InternalCertConnection.name;
	};

	requiresDOAGApproval = (selectedType: string) => {
		let oldRequest = !!this.state.stateForm.approverDOAG && !!this.state.stateForm.appEndorser
		return oldRequest && selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name;
	};

	requiresAppEndorserApproval = (selectedType: string) => {
		return selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name;
	};

	verifyApproverLevel = (selectedType: string) => {
		if(selectedType === OnPremPortOpeningRequestsTypes.InternetDMZ.name)
			return this.portOpeningRequestServiceOnPrem.verifyApproverLevel(this.state.stateForm?.approverDOAG?.userPrincipalName, false);
		return 
	};

	getAmountOfHosts(startIp:string, endIp:string){
		var startIP = new Address4(startIp).bigInteger();
		var endIP = new Address4(endIp).bigInteger();

		return (endIP - startIP) + 1;
	}

	isLargeSubnet(ip:string){
		if (ip.includes("/")) return new Address4(ip).subnetMask <= 26;
		else if(ip.includes("-")){
			var rangeIp = ip.split("-");
			return this.getAmountOfHosts(rangeIp[0],rangeIp[1]) >= 62;
		} else return false;
	}

	ipsContainsLargeSubnet(ips: string){
		var IPs = ips.split(";")
		return IPs.some(ip => this.isLargeSubnet(ip))   
	}

	assertIpsContainLargeSubnets(){
		var sourceIps = this.state.stateForm.ruleList.map(r => r.sourceIPs).flatMap(ips => ips.split(";"));	
		var destinationIPs = this.state.stateForm.ruleList.map(r => r.destinationIPs).flatMap(ips => ips.split(";"));	
		
		return sourceIps.some(ip => this.ipsContainsLargeSubnet(ip)) || destinationIPs.some(ip => this.ipsContainsLargeSubnet(ip))
	}

	// eslint-disable-next-line class-methods-use-this
	stateFormFromRequest(por: PortOpeningRequestOnPrem): IPORStateForm {
		return {
			direction: por.direction.name,
			contact: !!por.contactUser &&
				UserLookupObject.fromUser(por.contactUser),
			approverDOAG: !!por.approvingUser &&
				UserLookupObject.fromUser(por.approvingUser),
			trackerID: por.trackerID,
			cartaId: por.cartaId,
			appEndorser: por.appEndorser,
			firewall: por.firewall,
			firewallEnvironment: por.firewallEnvironment,
			ownerDelegate: por.approvingUser?.id,
			siteCoordination: por.siteCoordination,
			businessJustification: por.businessJustification,
			ruleList: por.onPremPortOpeningRules,
			comments: por.comments,
			changeTicketId: por.workflowInstance.currentWorkflowInstanceState.ticketId,
		};
	}
}
